import { useEffect, useMemo } from "react";

import { atom, useAtom, useAtomValue, useSetAtom } from "../../../app-jotai";
import { PdfFileController, WhiteboardFileAtom } from "../pdfFileState";

import { loadImage } from "../utils/load_image";

import { collabAPIAtom } from "../../../collab/Collab";

import type { ExcalidrawImperativeAPI } from "../../../../packages/excalidraw/types";

export const currentPageAtom = atom<[number, number]>([0, 0]);
export const totalPagesAtom = atom(0);
export const disablePreAtom = atom(true);
export const disableNextAtom = atom(false);
export const previousPageAtom = atom<number>(0);

export const useDocumentNavigator = () => {
  const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
  const [totalPages, setTotalPages] = useAtom(totalPagesAtom);
  const [disablePre, setDisablePre] = useAtom(disablePreAtom);
  const [disableNext, setDisableNext] = useAtom(disableNextAtom);

  const setNextPage = () => {
    setCurrentPage((prev) => [prev[1], prev[1] + 1]);
  };
  const setPrevPage = () => {
    setCurrentPage((prev) => [prev[1], prev[1] - 1]);
  };

  useEffect(() => {
    /// check for disabled pre
    if (currentPage[1] <= 1) {
      setDisablePre(true);
    } else {
      setDisablePre(false);
    }

    /// check for disabled next
    if (currentPage[1] >= totalPages) {
      setDisableNext(true);
    } else {
      setDisableNext(false);
    }
  }, [currentPage, totalPages, setDisablePre, setDisableNext]);

  return {
    disableNext,
    disablePre,
    currentPage,
    totalPages,
    setNextPage,
    setPrevPage,
    setTotalPages,
    setCurrentPage,
  };
};

export const useDocumentListener = ({
  excalidrawAPI,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}) => {
  const whiteboardPdf = useAtomValue(WhiteboardFileAtom);
  const pdfFileController = useMemo(
    () => new PdfFileController(whiteboardPdf),
    [whiteboardPdf],
  );
  // const pdfFileController = new PdfFileController(whiteboardPdf);
  const collabAPI = useAtomValue(collabAPIAtom);

  const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
  const setTotalPages = useSetAtom(totalPagesAtom);

  useEffect(() => {
    setCurrentPage([0, 1]);
    setTotalPages(pdfFileController.currentFile.totalPages);
    return () => {};
  }, [pdfFileController, setTotalPages, setCurrentPage]);

  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    if (pdfFileController.isCurrentFileEmpty) {
      return;
    }
    if (currentPage[0] === currentPage[1]) {
      return;
    }
    if (!collabAPI) {
      return;
    }

    loadImage(
      excalidrawAPI,
      pdfFileController.currentFile.pages[currentPage[1] - 1],
    )
      .then(() => {
        return collabAPI.fileManage.saveFiles({
          elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
          files: excalidrawAPI.getFiles(),
        });
      })
      .then(() => {
        collabAPI.syncElements(
          excalidrawAPI.getSceneElementsIncludingDeleted(),
        );
      });
  }, [currentPage, excalidrawAPI, pdfFileController, collabAPI]);
};
